import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { Observable, catchError, from, of, switchMap, tap, throwError } from 'rxjs';
import { IS_EXTERNAL_KEYCLOAK } from './http-constants';
import { ProjectSettingsState } from './state/project-settings.state';
import {
  findAndShowAlertFromCode,
  handleHttpErrorAlertCodes,
} from './utils/alert-code-utils/alert-code.utils';
import { isAuthenticatedRoute } from './utils/auth-checks.utils';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(
    private alertHandlerService: AlertHandlerService,
    private store: Store,
    private router: Router,
  ) {}
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.headers.has('x-finxone-skip-interceptors')) {
      const modifiedRequest = request.clone({
        headers: request.headers.delete('x-finxone-skip-interceptors'),
      });
      return next.handle(modifiedRequest);
    }
    return next.handle(request).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          this.handleSuccessResponse(event);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        return this.handleError(request, err);
      }),
    );
  }

  private handleSuccessResponse(successEvent: HttpResponse<unknown>) {
    if ((successEvent.body as any)?.alertCodes) {
      findAndShowAlertFromCode(
        this.store,
        this.router,
        this.alertHandlerService,
        (successEvent.body as any).alertCodes,
        (successEvent.body as any)?.data,
      );
    }
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    try {
      if (!(error.error instanceof Blob)) {
        throw error;
      }

      const text = await error.error.text();
      const parsed = JSON.parse(text);

      return new HttpErrorResponse({
        error: parsed,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
      });
    } catch {
      // Fallback if blob is not JSON
      return Promise.reject(error);
    }
  }

  handleError(request: HttpRequest<unknown>, error: HttpErrorResponse) {
    const token = sessionStorage.getItem('token');
    /**
     * @description: checking if token is not there and user tries to login authenticated path then it wont go inside and it wont display the error which are coming.
     */
    if (!token && isAuthenticatedRoute(window.location.pathname)) {
      return this.throwErrorFunc(null, error);
    }

    return from(this.parseBlobError(error)).pipe(
      catchError((error: HttpErrorResponse) => {
        return of(
          new HttpErrorResponse({
            error: error.error instanceof Blob ? {} : error.error,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          }),
        );
      }),

      switchMap((error: HttpErrorResponse) => {
        let errorMessage: string;
        const {
          message = '',
          alertCodes = [],
          data = {},
        } = (this.isJSONString(error?.error) ? JSON.parse(error.error) : error?.error || {}) ?? {};

        if (alertCodes?.length) {
          findAndShowAlertFromCode(this.store, this.router, this.alertHandlerService, alertCodes, data);
          errorMessage = message;
        } else {
          errorMessage = `Error Code: ${error.status}\nMessage: ${message ? message : 'Server Error'}`;

          const isExternalKeycloak: boolean = request.context.get(IS_EXTERNAL_KEYCLOAK);
          // 401s are excluded: TokenInterceptor (upstream) refreshes the token and replays
          // the request silently, or redirects to login itself if the session is really over
          if (!isExternalKeycloak && error.status !== undefined && error.status !== 0 && error.status !== 401)
            handleHttpErrorAlertCodes(
              this.alertHandlerService,
              this.store,
              this.router,
              error.status,
              errorMessage,
            );
        }

        return this.throwErrorFunc(errorMessage, error);
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private throwErrorFunc(errorMessage: any, error: HttpErrorResponse) {
    return throwError(() => {
      const httpError = `message: ${errorMessage}, requestUrl: ${error.url}, response code: ${
        error.status
      }, body: ${JSON.stringify(error.error)}`;
      const errorObj = new Error(httpError);
      Sentry.captureException(errorObj);
      return error;
    });
  }

  show(errorMessage: any) {
    const defaultOtherAlertsEnabled =
      this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.defaultOtherAlerts ?? true;

    if (defaultOtherAlertsEnabled) {
      this.alertHandlerService.showAlertFn('error', errorMessage);
    }
  }

  private isJSONString(str: string) {
    if (str == null) return false;
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }
}
